import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { Modal } from "react-bootstrap";
import { deleteBank } from "actions/bankActions";
import { createLoadingSelector, createErrorSelector } from "store/selectors";
import { userConstants, bankConstants } from "actions/types";
import { toast } from "react-toastify";
import { FiAlertTriangle } from "react-icons/fi";
import { getContributionAbilityAndEstimates } from "actions/contributionActions";
import {
  deleteEmployerLink,
  deleteEmployerLinkRequest,
} from "actions/userActions";
import type { RootState } from "types/state";

import IconCircle from "components/IconCircle";
import IconHeader from "components/IconHeader";
import IconSubheader from "components/IconSubheader";
import Button from "components/Button";

const actions = [
  userConstants.DELETE_USER_EMPLOYER_LINK,
  userConstants.DELETE_USER_EMPLOYER_LINK_REQ,
  bankConstants.DELETE_BANK,
];

const isRemovingSelector = createLoadingSelector(actions);

const errorSelector = createErrorSelector(actions);
const mapStateToProps = (state: RootState) => {
  return {
    isRemoving: isRemovingSelector(state),
    error: errorSelector(state),
  };
};

const mapDispatchToProps = {
  deleteEmployerLink,
  deleteBank,
  deleteEmployerLinkRequest,
  getContributionAbilityAndEstimates,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

interface OwnProps {
  onClose?: () => void;
  show?: boolean;
  isEmployer?: boolean;
  isLinkRequest?: boolean;
  sourceId?: string;
}

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  // written by _setEmployerLink via setState, never initialized
  employerLink?: unknown;
}

class RemoveSourceModal extends React.PureComponent<Props, State> {
  _removeInFlight = false;

  _setEmployerLink = (employerLink: unknown) => {
    this.setState({ employerLink });
  };

  _removeAccount = () => {
    if (this._removeInFlight) {
      return;
    }
    this._removeInFlight = true;

    let deleteFunction;

    if (this.props.isEmployer) {
      deleteFunction = this.props.isLinkRequest
        ? this.props.deleteEmployerLinkRequest
        : this.props.deleteEmployerLink;
    } else {
      deleteFunction = this.props.deleteBank;
    }

    void deleteFunction(
      this.props.client as ApolloClient<object>,
      this.props.sourceId as string
    )
      .then(() => {
        if (!this.props.error) {
          void this.props.getContributionAbilityAndEstimates(
            this.props.client as ApolloClient<object>
          );
          toast.success("Successfully removed account");
          this.props.onClose!();
        }
      })
      .finally(() => {
        this._removeInFlight = false;
      });
  };

  render() {
    const contributionType = this.props.isEmployer ? "payroll" : "recurring";
    const header = "Are you sure you want to remove this account?";

    const subheader = `Any ${contributionType} contribution associated with this account will be removed upon deletion.`;

    return (
      <Modal
        closeButton
        centered
        scrollable
        show={this.props.show}
        onHide={this.props.onClose}
        className="icon-modal"
      >
        <Modal.Body
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <IconCircle
            type="error"
            icon={<FiAlertTriangle color="white" stroke="#B12121" size={16} />}
          />
          <IconHeader variant="centeredText" headerText={header} />
          <IconSubheader
            variant="centered"
            subheader={subheader}
            style={{ display: "flex", textAlign: "center" }}
          />
        </Modal.Body>
        <Modal.Footer
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            borderTop: "none",
          }}
        >
          <Button
            color="red"
            name="submit"
            btnLabel="Remove Account"
            onClick={this._removeAccount}
            loading={this.props.isRemoving}
            withArrow
          />
        </Modal.Footer>
      </Modal>
    );
  }
}

export default connector(withApollo<Props>(RemoveSourceModal));
